import React, { useEffect, useState } from 'react';
import PageLayout from '../layouts/PageLayout';
import CardsCarousel from 'components/CardsCarousel';
import HomeFPCarousel from 'containers/HomeFPCarousel';
import TextPage from 'components/TextPage';
import BBHeroSection from 'components/BBHeroSection';
import Billboard from 'components/Billboard';
import NewsIcons from 'components/NewsIcons';
import ThinImagesSection from 'containers/ThinImagesSection';
import Brochure from 'components/Brochure';
import ServicesNavigation from 'components/ServicesNavigation';
import { getHomePageData } from '../api-sanity/api.js';
import RollingMenu from 'components/RollingMenu';
import { useRouteIsLoading } from 'context/RouteIsLoadingContext';
import usePageLoadProgress from 'hooks/usePageLoadProgress';
import { useSanityMetaTags } from 'hooks/useSanityMetaTags';

const pID = {
  P_1: '09c0639e-6ba9-47de-ab78-8ab302fa0ea2',
  P_2: '566f76d5-cd8b-4eea-84fd-13563faeb978',
  P_3: '1457321f-07ad-4e7a-b170-427cd9a53944',
  P_4: 'b3ebfffe-f8e2-4c8a-afda-34613d1de326',
  P_5: 'bc2759df-a0c7-4b52-9e05-710567eadeb4',
};

const homeIds = {
  cardsCarouselId: 'd3c64963-cf45-4bcd-8b51-33ee6be53e4f',
  rollingMenuId: '44466eab-9bf7-4c28-bf60-844d852da17d',
  homeSliderId: 'ebdb22d2-27b2-4e2e-b1bf-607d8ec56ece',
  newsIconsId: '2f617b5d-e73e-457a-874b-a0f5a93dd280',
  thinImagesId: '8b9f30e8-7d78-42d5-bba5-3df8078407bc',
  brochureId: '18217150-e817-407a-bf98-d73835e77d14',
  servicesSectionId: '186b8796-b883-4b65-bec0-4bfa515581b9',
};

const Home = () => {
  const [homeData, setHomeData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { setIsLoading: setRouteContextIsLoading } = useRouteIsLoading();

  // Fetch and apply meta tags from Sanity
  useSanityMetaTags('home');

  const { loadingProgress, isDataReady, apiDataResults } = usePageLoadProgress(
    (onProgress) => getHomePageData(homeIds, onProgress),
  );

  useEffect(() => {
    if (isDataReady && apiDataResults) {
      const [data] = apiDataResults;
      setHomeData(data);
      setIsLoading(false);
    }
  }, [isDataReady, apiDataResults]);

  useEffect(() => {
    setRouteContextIsLoading(isLoading);
  }, [isLoading, setRouteContextIsLoading]);

  const getParagraphById = (id) => {
    if (!homeData?.textPages) return null;
    return homeData.textPages.find((p) => p._id === id);
  };

  return (
    <PageLayout className='home-page'>
      <BBHeroSection
        data={homeData?.homePageFirstSection}
        loaderProgress={loadingProgress}
        isLoading={isLoading}
      />
      {!isLoading && getParagraphById(pID.P_1) && (
        <TextPage
          key={getParagraphById(pID.P_1)?._id}
          type={getParagraphById(pID.P_1)?.type}
          titleActive={getParagraphById(pID.P_1)?.titleActive}
          titleInactive={getParagraphById(pID.P_1)?.titleInactive}
          titleInactiveItalic={getParagraphById(pID.P_1)?.titleInactiveItalic}
          paragraphText={getParagraphById(pID.P_1)?.paragraphText}
          buttonText={getParagraphById(pID.P_1)?.buttonText}
          buttonLink={getParagraphById(pID.P_1)?.buttonLink}
        />
      )}
      {!isLoading && homeData?.newsIcons?.items && (
        <NewsIcons items={homeData.newsIcons.items} />
      )}
      {!isLoading && homeData?.homeSlider?.slides && (
        <HomeFPCarousel panelData={homeData.homeSlider} />
      )}
      {!isLoading && homeData?.thinImages?.data && (
        <ThinImagesSection
          cards={homeData.thinImages.data}
          titleGrotesk={homeData.thinImages.groteskTitle}
          titleItalic={homeData.thinImages.italicTitle}
          grid={homeData.thinImages.grid}
        />
      )}
      {!isLoading && getParagraphById(pID.P_2) && (
        <TextPage
          key={getParagraphById(pID.P_2)?._id}
          type={getParagraphById(pID.P_2)?.type}
          titleActive={getParagraphById(pID.P_2)?.titleActive}
          titleInactive={getParagraphById(pID.P_2)?.titleInactive}
          titleInactiveItalic={getParagraphById(pID.P_2)?.titleInactiveItalic}
          paragraphText={getParagraphById(pID.P_2)?.paragraphText}
          buttonText={getParagraphById(pID.P_2)?.buttonText}
          buttonLink={getParagraphById(pID.P_2)?.buttonLink}
        />
      )}
      {!isLoading && homeData?.cardsCarousel?.images && (
        <CardsCarousel cards={homeData.cardsCarousel.images} />
      )}
      {!isLoading && getParagraphById(pID.P_4) && (
        <TextPage
          key={getParagraphById(pID.P_4)?._id}
          type={getParagraphById(pID.P_4)?.type}
          titleActive={getParagraphById(pID.P_4)?.titleActive}
          titleInactive={getParagraphById(pID.P_4)?.titleInactive}
          titleInactiveItalic={getParagraphById(pID.P_4)?.titleInactiveItalic}
          paragraphText={getParagraphById(pID.P_4)?.paragraphText}
          buttonText={getParagraphById(pID.P_4)?.buttonText}
          buttonLink={getParagraphById(pID.P_4)?.buttonLink}
        />
      )}
      {!isLoading && homeData?.servicesSection && (
        <ServicesNavigation data={homeData.servicesSection} />
      )}
      {!isLoading && getParagraphById(pID.P_5) && (
        <TextPage
          key={getParagraphById(pID.P_5)?._id}
          type={getParagraphById(pID.P_5)?.type}
          titleActive={getParagraphById(pID.P_5)?.titleActive}
          titleInactive={getParagraphById(pID.P_5)?.titleInactive}
          titleInactiveItalic={getParagraphById(pID.P_5)?.titleInactiveItalic}
          paragraphText={getParagraphById(pID.P_5)?.paragraphText}
          buttonText={getParagraphById(pID.P_5)?.buttonText}
          buttonLink={getParagraphById(pID.P_5)?.buttonLink}
        />
      )}
      {!isLoading && homeData?.rollingMenu?.data && (
        <RollingMenu rollingMenu={homeData.rollingMenu} />
      )}
      {!isLoading && getParagraphById(pID.P_3) && (
        <TextPage
          key={getParagraphById(pID.P_3)?._id}
          type={getParagraphById(pID.P_3)?.type}
          titleActive={getParagraphById(pID.P_3)?.titleActive}
          titleInactive={getParagraphById(pID.P_3)?.titleInactive}
          titleInactiveItalic={getParagraphById(pID.P_3)?.titleInactiveItalic}
          paragraphText={getParagraphById(pID.P_3)?.paragraphText}
          buttonText={getParagraphById(pID.P_3)?.buttonText}
          buttonLink={getParagraphById(pID.P_3)?.buttonLink}
        />
      )}
      {!isLoading && homeData?.brochureData && (
        <Brochure data={homeData.brochureData} />
      )}
      {!isLoading && homeData?.homeLastPage?.title && (
        <Billboard data={homeData.homeLastPage} />
      )}
    </PageLayout>
  );
};

export default Home;
